import { Observable, Subject, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { FilterState } from '../filter-bar/filter-bar.component';
import { AnalyticsErrorKind } from '../../../../shared/components/sette/analytics-error-state/analytics-error-state.component';

// CLIPPING-threshold follow-up (2026-11-01): a number/delta is shown as soon as the relevant
// period has at least one mention - "insufficient data" text must never be the reason a real
// value is hidden (backend: ClientAnalyticsServiceImpl.MIN_MENTIONS_FOR_COMPARISON). Below this
// many mentions, a value/count is still shown in full, just visually paled with a small "low
// data" badge (hover explains why) - see LowDataBadgeComponent and SampleSizeNoteComponent, the
// two places that read it.
export const LOW_DATA_THRESHOLD = 5;

export function isLowDataCount(count: number | null | undefined): boolean {
	return count != null && count < LOW_DATA_THRESHOLD;
}

// Задача (Фаза 6): functions, not constants - this is a free module (no DI of its own), so every
// call site passes its own component's TranslateService, re-resolved on every call so a language
// switch takes effect immediately (same reason chart-theme's formatters take a `lang` parameter).

// CLIPPING-threshold follow-up (2026-11-01): the one remaining case where a KPI genuinely has
// nothing to show - the period itself has 0 mentions, so there is no average/percentage to
// compute at all. This is NOT "insufficient data" (that implied a value existed but was hidden
// below a floor, which no longer happens) - it is a plain empty period.
export function noMentionsLabel(translate: TranslateService): string {
	return translate.instant('ANALYTICS_GLOSSARY.NO_MENTIONS_IN_PERIOD');
}

// Single source of truth for "is this tag a competitor" - must match AnalyticsPanelComponent's
// own role mapping (anything that isn't the COMPANY tag is a competitor), not a strict
// equality check against the 'COMPETITOR' enum value. A tag with brandKind null/undefined (not
// yet classified, or a value the enum grows to later) still needs to count as a competitor here,
// the same way AnalyticsPanelComponent already treats it - otherwise this check can disagree with
// every other screen about whether competitors exist at all.
export function isCompetitorBrandKind(brandKind: string | null | undefined): boolean {
	return brandKind !== 'COMPANY';
}

// Пакет А #4: a 0 -> N jump has no defined percent change - "+0%"/"+100%" both read as a real,
// bounded number when the metric is actually brand new this period (nothing to divide by).
// Every KPI tile and table delta across the 7 Analytics screens must check this before computing
// a percent change from a possibly-zero previous value, and show newMetricLabel() instead.
export function isNewMetric(current: number, previous: number): boolean {
	return previous === 0 && current !== 0;
}

export function newMetricLabel(translate: TranslateService): string {
	return translate.instant('ANALYTICS_GLOSSARY.NEW_METRIC');
}

/** Same "was a compare period actually requested" check `buildAnalyticsFilterRequest` uses to
 *  decide whether to send compareStartDate/compareEndDate at all - duplicated here (not derived
 *  from that function's output) because most comparison call sites only have `filters`, not the
 *  built request. */
export function isCompareRequested(filters: FilterState | undefined | null): boolean {
	return !!filters && filters.compareType !== 'none' && !!filters.compareStartDate && !!filters.compareEndDate;
}

export interface ComparisonResolution {
	/** True once `previous` is a real, usable number - render the actual delta. */
	show: boolean;
	/** True when a comparison was requested but there is no usable previous value at all (the
	 *  compare period had 0 mentions) - callers render newMetricLabel() when the current value is
	 *  non-zero ("Ново"), or nothing, instead of silently comparing against the current value
	 *  (which reads as a fake "+0%"). */
	insufficient: boolean;
	previous: number | null;
}

/** The one rule every KPI tile, table delta, and drilldown figure across Overview, Coverage
 *  Dashboard, Coverage Analysis, Brands, Topics, Sentiment, and Media must apply before showing a
 *  period-over-period comparison. `hasEnoughCompareData` is the backend's own flag for whether
 *  the compare period cleared the 10-mention floor for this particular metric/row - trust it over
 *  a merely-non-null previousValue, since the two cases (no compare requested vs. requested-but-
 *  insufficient) must read differently to the user even though some DTOs null previousValue for
 *  both. */
export function resolveComparison(compareRequested: boolean, previousValue: number | null | undefined, hasEnoughCompareData: boolean | undefined): ComparisonResolution {
	if (!compareRequested) {
		return { show: false, insufficient: false, previous: null };
	}
	if (previousValue == null || hasEnoughCompareData === false) {
		return { show: false, insufficient: true, previous: null };
	}
	return { show: true, insufficient: false, previous: previousValue };
}

// Фаза 4: every one of the 7 screens' own analytics calls is given this long to answer (RxJS
// `timeout()` in each screen's fetch) before falling back to the generic error state - never a
// forever spinner. A `TimeoutError` has no `.status`, so classifyAnalyticsError below already
// buckets it as 'generic' (the same retryable case as a network failure or a 5xx) with no extra
// handling needed here.
export const ANALYTICS_REQUEST_TIMEOUT_MS = 30000;

// Same classification for all 7 screens' error handling - 403 (assertNewAnalyticsEnabled, new
// Analytics not enabled for this client) and 400 (a request-level validation failure, e.g. the
// 2-year range cap in validateDateRange) are shown as-is with no retry button
// (AnalyticsErrorStateComponent); everything else (network failure, 5xx, our own 30s timeout
// above) is 'generic' and retryable. The backend's 400 message is Spring's default error body
// (`server.error.include-message=always`) - already in English, rendered as-is rather than
// through i18n since it is dynamic, not a fixed key.
export function classifyAnalyticsError(err: unknown): AnalyticsErrorKind {
	const httpErr = err as { status?: number; error?: { message?: string } } | null | undefined;
	if (httpErr?.status === 403) {
		return { kind: 'forbidden' };
	}
	if (httpErr?.status === 400) {
		return { kind: 'badRequest', message: httpErr.error?.message || 'Bad request' };
	}
	return { kind: 'generic' };
}

export interface AnalyticsFetchResult<Res> {
	ok: boolean;
	data?: Res;
	error?: unknown;
}

// Задача 7: stale-response guard shared by every one of the 7 screens' fetch() - a quick filter
// change pushes a new request into `trigger`, and switchMap tears down the still-pending previous
// HTTP call instead of letting it resolve later and overwrite the newer filter's data. Errors are
// caught per-request (catchError inside the inner pipe, not on the outer stream) so one failed
// request doesn't terminate every later switchMap emission. Call `trigger.next(request)` from
// fetch(); subscribe exactly once, in the constructor, to the returned stream (with
// `takeUntilDestroyed()`), and set loading/data/error flags from the AnalyticsFetchResult.
export function createAnalyticsFetchStream<Req, Res>(trigger: Subject<Req>, call: (request: Req) => Observable<Res>): Observable<AnalyticsFetchResult<Res>> {
	return trigger.pipe(
		switchMap((request) =>
			call(request).pipe(
				map((data): AnalyticsFetchResult<Res> => ({ ok: true, data })),
				catchError((error): Observable<AnalyticsFetchResult<Res>> => of({ ok: false, error }))
			)
		)
	);
}

// The Filters drawer's media-type ids ('Web'|'TV'|'Radio'|'Print'|'Social') are a UI convenience
// predating the real backend - the backend's Medium.type enum spells two of them differently
// ('PRINTED', not 'Print') and uppercases the rest.
export const MEDIA_TYPE_TO_BACKEND: Record<string, string> = {
	Web: 'WEB',
	TV: 'TV',
	Radio: 'RADIO',
	Print: 'PRINTED',
	Social: 'SOCIAL',
};

export function toIsoDate(date: Date): string {
	const y = date.getFullYear();
	const m = `${date.getMonth() + 1}`.padStart(2, '0');
	const d = `${date.getDate()}`.padStart(2, '0');
	return `${y}-${m}-${d}`;
}

export interface AnalyticsFilterRequestFields {
	startDate: string;
	endDate: string;
	compareStartDate?: string;
	compareEndDate?: string;
	tagIds?: string[];
	mediaTypes?: string[];
	mediaOutlets?: string[];
	authors?: string[];
	sentiments?: string[];
	sourceTypes?: string[];
	topics?: string[];
}

// Builds the shared filter fields every analytics screen endpoint (Overview + the 6 connected
// screens) accepts from the Filters drawer's current FilterState. mediaOutlets/authors are already
// backend ids (populated from GET /v1/client/analytics/filter-options by the Filters drawer), so
// unlike mediaTypes they need no label mapping before being sent through.
export function buildAnalyticsFilterRequest(filters: FilterState): AnalyticsFilterRequestFields | null {
	if (!filters?.startDate || !filters?.endDate) {
		return null;
	}
	const hasCompare = filters.compareType !== 'none' && !!filters.compareStartDate && !!filters.compareEndDate;
	return {
		startDate: toIsoDate(filters.startDate),
		endDate: toIsoDate(filters.endDate),
		compareStartDate: hasCompare ? toIsoDate(filters.compareStartDate!) : undefined,
		compareEndDate: hasCompare ? toIsoDate(filters.compareEndDate!) : undefined,
		tagIds: filters.brands?.length ? filters.brands : undefined,
		mediaTypes: filters.mediaTypes?.length ? filters.mediaTypes.map((t) => MEDIA_TYPE_TO_BACKEND[t]).filter((t): t is string => !!t) : undefined,
		mediaOutlets: filters.mediaOutlets?.length ? filters.mediaOutlets : undefined,
		authors: filters.authors?.length ? filters.authors : undefined,
		sentiments: filters.sentiments?.length ? filters.sentiments : undefined,
		sourceTypes: filters.sourceTypes?.length ? filters.sourceTypes : undefined,
		topics: filters.topics?.length ? filters.topics : undefined,
	};
}
